/* eslint-disable jsx-a11y/anchor-is-valid */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React from 'react';
import { WorkspaceRoutes } from 'types/common';
import {
  HUD_1,
  NARRATIVE,
  NOTIFICATIONS,
  ROUTE_DISBURSEMENTS_AND_RECEIPTS,
} from 'utils/constants';

import Link from './link';
import { CurrentUser } from 'contexts/userContext';
import { useCheckUserPermissions } from 'hooks/useChekUserPermissions';

const useStyles = makeStyles((theme: any) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.2),
    paddingTop: theme.spacing(1.2),
    paddingBottom: theme.spacing(1.2),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary.main,
  },
  headerIcon: {
    color: theme.palette.primary.main,
    width: theme.spacing(2),
    height: theme.spacing(2),
  },
  container: { padding: theme.spacing(1) },
  subtitle: {
    color: theme.palette.primary.light,
    marginLeft: theme.spacing(2),
  },
  section: {
    padding: 0,
    margin: 0,
  },
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    color: theme.accent.main,
    padding: theme.spacing(1, 2),
  },
  sectionLabelIcon: { marginRight: theme.spacing(1) },
  sectionDetails: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& > div': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
  },
  linkCharges: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chargesIcon: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.accent.main,
    color: theme.palette.common.black,
  },
  muiLink: {
    color: theme.palette.common.white,
    opacity: 0.45,
    marginLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '&:hover': {
      color: theme.palette.common.white,
      opacity: 0.8,
    },
  },
}));

type OpenSidemenuProps = {
  t: TFunction;
  setSidemenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleViewChange: (view: WorkspaceRoutes, param?: string) => void;
  orderType: string;
  transactionType: string;
  currentUser: CurrentUser | null;
};

const OpenSidemenu = ({
  t,
  setSidemenuOpen,
  handleViewChange,
  orderType,
  transactionType,
  currentUser
}: OpenSidemenuProps) => {
  const classes = useStyles();
  const hideAIStuff = true;
  const { hasPermission } = useCheckUserPermissions(currentUser);
  const hasLedgerPermission = hasPermission('createOrderLedgerEntry');
  const hasTransferPermission = hasPermission('createTransferRequest');
  const showPaymentsMenu = hasLedgerPermission || hasTransferPermission;
  return (
    <>
      <Box className={classes.header}>
        <Typography>{t('orders:order-menu')}</Typography>

        <IconButton
          className={classes.headerIcon}
          onClick={(e) => {
            e.stopPropagation();
            setSidemenuOpen(false);
          }}
          size="large"
        >
          <FontAwesomeIcon icon="caret-square-left" size="xs" />
        </IconButton>
      </Box>

      <Box className={classes.container}>
        <Box className={classes.section}>
          <Typography variant="body2" className={classes.sectionLabel}>
            <FontAwesomeIcon icon="briefcase" className={classes.sectionLabelIcon} />
            {t('common:general')}
          </Typography>

          <Box className={classes.sectionDetails}>
            <Link view="overview" onClick={() => handleViewChange('overview')}>
              {t('orders:overview')}
            </Link>

            {orderType !== HUD_1 && hasPermission('createOrderLedgerEntry') && (
            <Link view="cash-to-close" onClick={() => handleViewChange('cash-to-close')}>
              {t('disclosure:cash-to-close')}
            </Link>
            )}
            {hasPermission('createOrderLedgerEntry') &&
            <Link view="charges" onClick={() => handleViewChange('charges')}>
              {t('common:charges')}
            </Link>}
            {hasPermission('updateOrderConfiguration') &&
            <Link view="dates-and-config" onClick={() => handleViewChange('dates-and-config')}>
              {t('orders:date-and-config')}
            </Link>}
            {hasPermission('createOrderFile') &&
            <Link view="documents" onClick={() => handleViewChange('documents')}>
              {t('orders:documents')}
            </Link>}
            {hasPermission('createOrderLedgerEntry') &&
            <Link
              view="earnest-and-commissions"
              onClick={() => handleViewChange('earnest-and-commissions')}
            >
              {t('earnestAndCommissions:earnest-and-commissions')}
            </Link>}

            {hasPermission('createOrderLoan') &&
            <Link view="loans" onClick={() => handleViewChange('loans')}>
              {t('loan:loans')}
            </Link>}
            {hasPermission('createOrderMortgage') &&
            <Link view="payoffs" onClick={() => handleViewChange('payoffs')}>
              {t('payoffs:payoffs')}
            </Link>}
            {hasPermission('createOrderNote') &&
            <Link view="notes" onClick={() => handleViewChange('notes')}>
              {t('notes:notes')}
            </Link>}
            {hasPermission('sendNotification') &&
            <Link view={NOTIFICATIONS} onClick={() => handleViewChange(NOTIFICATIONS)}>
              {t('messages:notifications')}
            </Link>}
            {hasPermission('createOrderAssociation') &&
            <Link view="parties" onClick={() => handleViewChange('parties')}>
              {t('parties:parties')}
            </Link>}
            {hasPermission('createOrderLedgerEntry') &&
            <Link
              view="policy-info-and-rates"
              onClick={() => handleViewChange('policy-info-and-rates')}
            >
              {t('policyInfoAndRates:policy-info-and-rates')}
            </Link>}
            {orderType !== HUD_1 && hasPermission('updateOrderProjected') && (
            <Link
              view="projected-payments"
              onClick={() => handleViewChange('projected-payments')}
            >
              {t('disclosure:projected-payments')}
            </Link>
            )}
            {hasPermission('createOrderProperty') &&
            <Link view="properties" onClick={() => handleViewChange('properties')}>
              {t('orders:properties')}
            </Link>}
            {hasPermission('createOrderProration') &&
            <Link
              view="taxes-and-prorations"
              onClick={() => handleViewChange('taxes-and-prorations')}
            >
              {t('taxesAndProrations:taxes-and-prorations')}
            </Link>}
          </Box>
        </Box>
        <Box>
          {showPaymentsMenu && (
          <Box className={classes.section}>
            <Typography variant="body2" className={classes.sectionLabel}>
              <FontAwesomeIcon
                icon="money-bill-wave-alt"
                className={classes.sectionLabelIcon}
              />
              {t('payments:payments')}
            </Typography>

            <Box className={classes.sectionDetails}>
              {hasLedgerPermission &&
              <Link
                view="payments"
                param="accounting"
                onClick={() => handleViewChange('payments', 'accounting')}
              >
                {t('accounting:accounting')}
              </Link>}
              {hasLedgerPermission &&
              <Link
                view={ROUTE_DISBURSEMENTS_AND_RECEIPTS}
                onClick={() => handleViewChange(ROUTE_DISBURSEMENTS_AND_RECEIPTS)}
              >
                {t('payments:disbursements-and-receipts')}
              </Link>}
              {hasLedgerPermission &&
              <Link
                view="revenue"
                onClick={() => handleViewChange('revenue')}
              >
                {t('revenue:title')}
              </Link>}
              {hasLedgerPermission &&
              <Link
                view="payments"
                param="settlement"
                onClick={() => handleViewChange('payments', 'settlement')}
              >
                {t('payments:settlement')}
              </Link>}
              {hasTransferPermission &&
              <Link
                view="payments"
                param="wire-transfers"
                onClick={() => handleViewChange('payments', 'wire-transfers')}
              >
                {t('payments:wire-tranfers')}
              </Link>}
            </Box>
          </Box>
          )}
          {hasPermission('updateOrderTokens') && (
          <Box className={classes.section}>
            <Typography variant="body2" className={classes.sectionLabel}>
              <FontAwesomeIcon icon="file-alt" className={classes.sectionLabelIcon} />
              {t('closing:closing')}
            </Typography>

            <Box className={classes.sectionDetails}>
              <Link
                view="closing"
                param="schedule-closing"
                onClick={() => handleViewChange('closing', 'schedule-closing')}
              >
                {t('closing:schedule-closing')}
              </Link>
            </Box>
          </Box>
          )}
          {!hideAIStuff && (
          <Box className={classes.section}>
            <Typography variant="body2" className={classes.sectionLabel}>
              <FontAwesomeIcon icon="file-alt" className={classes.sectionLabelIcon} />
              {t('common:ai')}
            </Typography>

            <Box className={classes.sectionDetails}>
              <Link view={NARRATIVE} onClick={() => handleViewChange(NARRATIVE)}>
                {t('orders:narrative')}
              </Link>
            </Box>
          </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default OpenSidemenu;
